import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Loading from "../Common/Loading";
import CustomAlert from "../Common/CustomAlert";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK || "https://staging.sysadmin.elearnoncloud.com/";

interface LessonPerformanceRow {
  DICCode: string;
  LessonCode: string;
  LessonName: string;
  SessionId: string;
  Score: string;
  TimeSpent: string;
  EndDateTime_Local: string;
  FirstQnNo: string;
  FirstLsnCode?: string;   // lesson of the first question (tests only)
}
// Lifted directly from the VB page's <style> block — the .lesson-* rules only.
// (The qnsval_/qns_ rules belong to the server-rendered per-question
// breakdown table — <%= QnsTable %> — which isn't reproduced here; see the
// note near the bottom of this file.)
const LESSON_STYLES = `
.lesson-container {
    width: 70%;
    background-color: #dbe5f1;
    margin-top: 10px;
    font-size: 16px;
}
.lesson-title {
    text-align: center;
    font-weight: bold;
    font-size: 16px;
    padding: 6px;
    background-color: #b8cce4;
    border-top: 1px solid #5c84c3;
    border-left: 1px solid #5c84c3;
    border-right: 1px solid #5c84c3;
    color: #1f3a6d;
}
.lesson-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}
.lesson-header th {
    background-color: #b8cce4;
    color: #1f3a6d;
    border: 1px solid #5c84c3;
    padding: 5px;
    font-weight: bold;
    height: 25px;
    text-align: left;
}
.lesson-row td {
    border: 1px solid #5c84c3;
    padding: 5px;
    background-color: #edf2fb;
    height: 25px;
    font-size: 14px;
}
.lesson-row:hover td {
    background-color: #9dc3e6;
    cursor: pointer;
}
.lesson-row.selected td {
    background-color: orange !important;
}
.center {
    text-align: center;
}
`;

const getInputValue = (date: Date) => date.toISOString().slice(0, 10);

const readOnlyFieldStyle: React.CSSProperties = {
  color: "blue",
  fontFamily: "Arial",
  fontSize: "14pt",
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

const EL_StudentPerformanceReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const schoolCode = searchParams.get("SchoolCode") || searchParams.get("S") || "";
  const familyCode = searchParams.get("FamilyCode") || searchParams.get("familycode") || searchParams.get("F") || "";
  const loginId = searchParams.get("Login") || searchParams.get("L") || "";
  // VB: the textbox labeled "Student Name:" is actually bound to txtlogin —
  // matching that (slightly mislabeled) behavior from the original page.
  const studentName = searchParams.get("Fname") || searchParams.get("L") || searchParams.get("StudentName") || "";
  const country = searchParams.get("Country") || "";

  const today = new Date();
  const defaultTo = getInputValue(today);
  const defaultFrom = getInputValue(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));

  const [fromDate, setFromDate] = useState(searchParams.get("FromDate") || searchParams.get("From") || defaultFrom);
  const [toDate, setToDate] = useState(searchParams.get("ToDate") || searchParams.get("To") || defaultTo);
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState<LessonPerformanceRow[]>([]);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: "" });
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null); // VB: fn_SelectRow(this)


  useEffect(() => {
    document.body.style.backgroundColor = '#fff'
  }, []);
  const fetchLessons = useCallback(async () => {
    if (!schoolCode || !familyCode || !loginId) {
      setAlertInfo({
        show: true,
        message: "Missing SchoolCode, FamilyCode or Login in the URL.",
      });
      setLoading(false);
      return;
    }

    const params = new URLSearchParams({
      SchoolCode: schoolCode,
      FamilyCode: familyCode,
      Login: loginId,
      FromDate: fromDate || defaultFrom,
      ToDate: toDate || defaultTo,
      Country: country,
    });

    setLoading(true);
    setLessons([]);
    setSelectedRowKey(null);
    setAlertInfo({ show: false, message: "" });

    try {
      const resp = await fetch(
        `${API_BASE_URL}api/get_student_performance_report/?${params.toString()}`
      );

      if (!resp.ok) {
        const errorText = await resp.text().catch(() => resp.statusText);
        throw new Error(`${resp.status} ${errorText}`);
      }

      const result = await resp.json();

      if (result?.status !== "success") {
        throw new Error(
          result?.message || "Failed to load student performance."
        );
      }

      setLessons(result.data?.lessons || []);
    } catch (err: any) {
      setAlertInfo({
        show: true,
        message:
          err.message || "Unable to load student performance.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    schoolCode,
    familyCode,
    loginId,
    fromDate,
    toDate,
    country,
    defaultFrom,
    defaultTo,
  ]);

  // Load once when the report opens. Date changes only update the
  // controls; the API is called when the user clicks Go.
  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGo = () => {
    fetchLessons();
  };

  // Browsers only allow window.close() on tabs/windows opened via script
  // (window.open) or ones with no other history — a page reached by normal
  // navigation can't be closed this way, and the browser silently ignores
  // the call. Try it, then fall back to something useful if it didn't work.
  const handleClose = () => {
    window.close();
    setTimeout(() => {
      if (!window.closed) {
        if (window.history.length > 1) {
          window.history.back();
        } else if (window.opener) {
          window.location.href = "about:blank";
        } else {
          setAlertInfo({
            show: true,
            message: "This tab can't be closed automatically — please close it manually.",
          });
        }
      }
    }, 150);
  };


  // const handleClose = () => {
  //   window.close();
  // };



  const handleLessonSelect = (row: LessonPerformanceRow, rowKey: string) => {
    setSelectedRowKey(rowKey);

    const params = new URLSearchParams({
      QnNo: row.FirstQnNo || "",
      SchoolCode: schoolCode,
      FamilyCode: familyCode,
      Login: loginId,
      Title: row.LessonCode || "",
      DICCode: row.DICCode || "",
      Country: country,
      from: "EL_Studentperformance",
    });
    if (row.FirstLsnCode && row.FirstLsnCode !== "0" && row.FirstLsnCode !== row.LessonCode) {
      params.set("LsnCode", row.FirstLsnCode);
    }
    const questionViewerUrl =
      `/LessonBrowser/StudentQuestionViewer?${params.toString()}`;

    window.open(
      questionViewerUrl,
      "_blank",
      "width=1200,height=800,scrollbars=yes,resizable=yes"
    );
  };

  return (
    <div style={{ fontFamily: "Arial", fontSize: "13pt", width: "100%", overflow: "hidden" }}>
      <style>{LESSON_STYLES}</style>
      {loading && <Loading />}

      {/* tblTitle */}
      <table border={0} cellPadding={4} cellSpacing={0} width="100%">
        <tbody>
          <tr>
            <td
              align="center"
              style={{ fontFamily: "Arial", fontSize: "16pt", color: "white", backgroundColor: "teal" }}
            >
              STUDENT PERFORMANCE ANALYSIS
            </td>
          </tr>
        </tbody>
      </table>

      {/* tblFilter1 */}
      <table border={0} cellPadding={3} cellSpacing={0} width="100%">
        <tbody>
          <tr>
            <td align="left">
              Schoolcode :&nbsp;
              <input
                readOnly
                value={schoolCode}
                onFocus={(e) => e.target.blur()}
                style={{
                  ...readOnlyFieldStyle,
                  width: `${Math.max(schoolCode.length + 2, 6)}ch`,
                  minWidth: "90px",
                }}
              />
              Familycode :&nbsp;
              <input
                readOnly
                value={familyCode}
                onFocus={(e) => e.target.blur()}
                style={{
                  ...readOnlyFieldStyle,
                  width: `${Math.max(familyCode.length + 2, 6)}ch`,
                }}
              />
              Student Name:&nbsp;
              <input
                readOnly
                value={studentName}
                onFocus={(e) => e.target.blur()}
                style={{
                  ...readOnlyFieldStyle,
                  width: `${Math.max(studentName.length + 2, 8)}ch`,
                }}
              />
              From :
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ height: "25px", fontSize: "16px" }}
              />
              TO :
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ height: "25px", fontSize: "16px" }}
              />
              &nbsp;
              <button onClick={handleGo} style={{ fontSize: "16px", width: "48px", height: "30px" }}>
                Go
              </button>
            </td>
            <td align="right">
              <button
                style={{ fontFamily: "Arial", fontSize: "14pt", height: "30px" }}
                onClick={handleClose}
              >
                CLOSE
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      {/* lesson-container */}
      <div className="lesson-container">
        <div className="lesson-title">Lesson Report</div>
        <table className="lesson-table">
          <thead>
            <tr className="lesson-header">
              <th>DicName</th>
              <th>Lesson Code</th>
              <th>Lesson Name</th>
              <th>Attempt</th>
              <th>Score</th>
              <th>Total Time Spent</th>
              <th>Date &amp; Time</th>
            </tr>
          </thead>
          <tbody>
            {lessons.length === 0 && !loading ? (
              <tr>
                <td colSpan={7} className="center" style={{ padding: "10px" }}>
                  No lesson performance records found for the selected date range.
                </td>
              </tr>
            ) : (
              lessons.map((row, index) => {
                const rowKey = `${row.DICCode}-${row.LessonCode}-${index}`;
                return (
                  <tr
                    key={rowKey}
                    className={`lesson-row${selectedRowKey === rowKey ? " selected" : ""}`}
                    onClick={() => handleLessonSelect(row, rowKey)}
                  >
                    <td>{row.DICCode || ""}</td>
                    <td>{row.LessonCode || ""}</td>
                    <td>{row.LessonName || ""}</td>
                    <td>{row.SessionId || ""}</td>
                    <td>{row.Score || ""}</td>
                    <td>{row.TimeSpent || ""}</td>
                    <td>{row.EndDateTime_Local || ""}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/*
        VB also renders <%= QnsTable %> right here — a server-generated,
        scrollable per-question breakdown for whichever lesson row is
        clicked (built via the same legacy clsGrid scrollable-table control
        used elsewhere, wired up through fn_SelectRow / __doPostBack).
        The codebehind (EL_Studentperformance.aspx.vb) and Studentperformance.js
        weren't included, so this piece isn't reproduced — row selection is
        wired up (click highlights the row, matching fn_SelectRow's visual
        effect) but doesn't yet fetch/show question-level detail. Share those
        two files and I'll wire the rest up to match exactly.
      */}

      {alertInfo.show && (
        <CustomAlert message={alertInfo.message} onClose={() => setAlertInfo({ show: false, message: "" })} />
      )}
    </div>
  );
};

export default EL_StudentPerformanceReport;